import React, { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Eye, EyeOff } from 'lucide-react'
import { api } from '../lib/api'
import { setTokens } from '../lib/utils'

type Props = { onLogin: () => void }

export default function LoginPage({ onLogin }: Props) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  
  // Get redirect params for checkout flow
  const planSlug = searchParams.get('plan')
  const interval = searchParams.get('interval') || 'month'
  const redirectTo = searchParams.get('redirect')
  
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  // 2FA state
  const [show2FAModal, setShow2FAModal] = useState(false)
  const [tempToken, setTempToken] = useState('')
  const [totpCode, setTotpCode] = useState('')
  const [twoFAError, setTwoFAError] = useState<string | null>(null)
  const [verifying2FA, setVerifying2FA] = useState(false)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      // Nouvel endpoint avec support 2FA
      const { data } = await api.post('/fapi/auth/jwt/login-2fa', {
        email,
        password,
      })
      
      // Si 2FA requise, ouvrir la modal
      if (data.requires_2fa) {
        setTempToken(data.temp_token)
        setShow2FAModal(true)
        setLoading(false)
        return
      }
      
      // Sinon, login normal
      setTokens(data.access_token, null)
      
      // Récupérer user info
      const userResponse = await api.get('/fapi/users/me', {
        headers: { 'Authorization': `Bearer ${data.access_token}` }
      })
      
      try {
        localStorage.setItem('user', JSON.stringify(userResponse.data))
      } catch {}
      
      onLogin()
      
      // Redirect to checkout if coming from pricing page
      if (redirectTo === 'checkout' && planSlug) {
        navigate(`/checkout?plan=${planSlug}&interval=${interval}`)
      } else {
        navigate('/')
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Erreur de connexion')
    } finally {
      setLoading(false)
    }
  }

  const verify2FA = async (e: React.FormEvent) => {
    e.preventDefault()
    setTwoFAError(null)
    setVerifying2FA(true)
    try {
      const { data } = await api.post('/fapi/auth/jwt/verify-2fa', {
        temp_token: tempToken,
        code: totpCode,
      })
      
      setTokens(data.access_token, null)
      
      // Récupérer user info
      const userResponse = await api.get('/fapi/users/me', {
        headers: { 'Authorization': `Bearer ${data.access_token}` }
      })
      
      try {
        localStorage.setItem('user', JSON.stringify(userResponse.data))
      } catch {}
      
      onLogin()
      
      // Redirect to checkout if coming from pricing page
      if (redirectTo === 'checkout' && planSlug) {
        navigate(`/checkout?plan=${planSlug}&interval=${interval}`)
      } else {
        navigate('/')
      }
    } catch (err: any) {
      setTwoFAError(err?.response?.data?.detail || 'Code invalide')
    } finally {
      setVerifying2FA(false)
    }
  }

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-10 px-4 py-12 sm:py-16">
      <div className="relative grid gap-10 overflow-hidden rounded-3xl border border-emerald-100/80 bg-white/90 shadow-2xl shadow-emerald-500/10 backdrop-blur dark:border-emerald-900/40 dark:bg-zinc-950/90 dark:shadow-none lg:grid-cols-[1.1fr_1fr]">
        <div className="relative hidden px-8 py-12 sm:px-12 sm:py-16 lg:block">
          <div aria-hidden className="absolute inset-0 -z-10 bg-gradient-to-br from-emerald-500/25 via-emerald-400/15 to-transparent" />
          <div aria-hidden className="absolute -right-24 -top-24 h-64 w-64 rounded-full bg-emerald-400/15 blur-3xl dark:bg-emerald-500/20" />
          <div className="relative space-y-6 text-zinc-900 dark:text-zinc-100">
            <span className="inline-flex items-center gap-2 rounded-full border border-emerald-200/70 bg-white/80 px-4 py-1 text-xs font-semibold uppercase tracking-[0.25em] text-emerald-600 shadow-sm dark:border-emerald-900/50 dark:bg-emerald-900/40 dark:text-emerald-300">
              Totemium
            </span>
            <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Connectez-vous à votre cockpit IA</h1>
            <p className="text-base text-zinc-600 dark:text-zinc-300">
              Retrouvez vos conversations, vos contenus indexés et tout le pilotage de vos parcours apprenants. Totemium unifie les données pédagogiques et vous aide à délivrer des expériences personnalisées.
            </p>
            <ul className="space-y-3 text-sm text-zinc-600 dark:text-zinc-300">
              <li className="flex items-start gap-3">
                <span className="mt-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-200">1</span>
                <div>
                  <strong className="font-semibold">Centralisez vos savoirs</strong>
                  <p>Média, transcripts et sujets consolidés dans un seul hub.</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-200">2</span>
                <div>
                  <strong className="font-semibold">Orchestrez vos widgets</strong>
                  <p>Déployez Totemium sur vos sites en quelques clics.</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-200">3</span>
                <div>
                  <strong className="font-semibold">Suivez la performance</strong>
                  <p>Analysez l’engagement et optimisez vos parcours continus.</p>
                </div>
              </li>
            </ul>
            <div className="flex flex-wrap gap-3 pt-2 text-sm">
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 rounded-full border border-emerald-200/70 bg-white/80 px-4 py-2 font-medium text-emerald-700 shadow-sm transition hover:bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200 dark:hover:bg-emerald-900/70"
              >
                Découvrir les offres
              </Link>
              <a
                href="mailto:support@totemium.com"
                className="inline-flex items-center gap-2 text-zinc-500 transition hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200"
              >
                Besoin d’aide ?
              </a>
            </div>
          </div>
        </div>
        <div className="relative flex items-center justify-center bg-white/90 px-8 py-12 backdrop-blur dark:bg-zinc-950/90 sm:px-12">
          <div className="w-full max-w-sm space-y-6">
            <div className="space-y-2 text-center">
              <h2 className="text-2xl font-semibold text-zinc-900 dark:text-white">Connexion</h2>
              <p className="text-sm text-zinc-500 dark:text-zinc-400">Accédez à votre console Totemium.</p>
            </div>
            <form onSubmit={submit} className="space-y-5">
              <div className="space-y-2">
                <label className="text-sm font-medium text-zinc-600 dark:text-zinc-300" htmlFor="email">Email</label>
                <input
                  id="email"
                  className="w-full rounded-xl border border-emerald-100/80 bg-white/90 px-4 py-2 text-sm text-zinc-900 shadow-sm transition focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200 dark:border-emerald-900/50 dark:bg-zinc-950/80 dark:text-zinc-100 dark:focus:border-emerald-500 dark:focus:ring-emerald-900/60"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-zinc-600 dark:text-zinc-300" htmlFor="password">Mot de passe</label>
                <div className="relative">
                  <input
                    id="password"
                    className="w-full rounded-xl border border-emerald-100/80 bg-white/90 px-4 py-2 pr-10 text-sm text-zinc-900 shadow-sm transition focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200 dark:border-emerald-900/50 dark:bg-zinc-950/80 dark:text-zinc-100 dark:focus:border-emerald-500 dark:focus:ring-emerald-900/60"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 dark:text-zinc-500 dark:hover:text-zinc-300 transition"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              {error && <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200">{error}</div>}
              <div className="flex items-center justify-between text-xs">
                <Link to="/register" className="text-emerald-600 hover:underline dark:text-emerald-400">
                  Créer un compte
                </Link>
                <Link to="/forgot-password" className="text-zinc-500 hover:underline dark:text-zinc-400">
                  Mot de passe oublié ?
                </Link>
              </div>
              <button
                disabled={loading}
                className="w-full rounded-full bg-emerald-500 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-emerald-500/40 transition hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {loading ? 'Connexion…' : 'Se connecter'}
              </button>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-zinc-200 dark:border-zinc-800"></div>
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="bg-white px-2 text-zinc-500 dark:bg-zinc-950 dark:text-zinc-400">Ou continuez avec</span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => window.location.href = '/fapi/auth/google/authorize'}
                  className="flex items-center justify-center gap-2 rounded-full border border-zinc-200 bg-white px-4 py-2 text-sm font-medium text-zinc-700 shadow-sm transition hover:bg-zinc-50 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800"
                >
                  <svg className="h-4 w-4" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Google
                </button>
                <button
                  type="button"
                  onClick={() => window.location.href = '/fapi/auth/github/authorize'}
                  className="flex items-center justify-center gap-2 rounded-full border border-zinc-200 bg-white px-4 py-2 text-sm font-medium text-zinc-700 shadow-sm transition hover:bg-zinc-50 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800"
                >
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                  </svg>
                  GitHub
                </button>
              </div>
            </form>
            <p className="text-center text-xs text-zinc-400 dark:text-zinc-500">
              Accès réservé aux administrateurs et éditeurs Totemium.
            </p>
          </div>
        </div>
      </div>

      {/* Modal 2FA */}
      {show2FAModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="relative mx-4 w-full max-w-md rounded-2xl border border-zinc-200 bg-white p-6 shadow-2xl dark:border-zinc-800 dark:bg-zinc-900">
            <h2 className="mb-2 text-xl font-semibold text-zinc-900 dark:text-zinc-100">
              Authentification à deux facteurs
            </h2>
            <p className="mb-6 text-sm text-zinc-600 dark:text-zinc-400">
              Saisissez le code à 6 chiffres généré par votre application d'authentification (Google Authenticator, Authy, etc.)
            </p>

            <form onSubmit={verify2FA} className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-zinc-700 dark:text-zinc-300">
                  Code de vérification
                </label>
                <input
                  type="text"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="w-full rounded-lg border border-zinc-300 bg-white px-4 py-2.5 text-center text-lg font-mono tracking-widest text-zinc-900 placeholder:text-zinc-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100 dark:placeholder:text-zinc-600"
                  autoFocus
                  maxLength={6}
                  required
                />
              </div>

              {twoFAError && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-400">
                  {twoFAError}
                </div>
              )}

              <div className="text-xs text-zinc-600 dark:text-zinc-400">
                💡 <strong>Astuce :</strong> Vous pouvez aussi utiliser un code de récupération si vous n'avez plus accès à votre application d'authentification.
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShow2FAModal(false)
                    setTotpCode('')
                    setTwoFAError(null)
                  }}
                  className="flex-1 rounded-lg border border-zinc-300 bg-white px-4 py-2.5 text-sm font-medium text-zinc-700 transition hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700"
                  disabled={verifying2FA}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={verifying2FA || totpCode.length !== 6}
                  className="flex-1 rounded-lg bg-emerald-500 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-emerald-500/40 transition hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {verifying2FA ? 'Vérification…' : 'Vérifier'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
